import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, ScrollText } from "lucide-react";
import Navbar from "@/components/Navbar";

const REDACTED = ["██████████████", "█████████████", "██████████████████"];

const WHISPERS = [
  "The first block was never signed by a name.",
  "Ranks don't lie. People do. We only count the chain.",
  "Every treasury is public. Every motive isn't.",
  "Some flips were predicted. None were promised.",
  "The crown is rented. Never owned.",
];

export default function AboutPage() {
  return (
    <div className="min-h-screen grain">
      <Navbar />
      <About />
      {/* footer is rendered globally in App.js */}
    </div>
  );
}

function About() {
  const [revealed, setRevealed] = useState(false);
  const [whisper, setWhisper] = useState(null);

  const askOracle = () => {
    setWhisper(null);
    setTimeout(() => setWhisper(WHISPERS[Math.floor(Math.random() * WHISPERS.length)]), 600);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 pb-24">
      {/* Hero */}
      <section className="pt-16 text-center sm:pt-24">
        <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-600">About</div>
        <h1 className="mt-4 font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter sm:text-7xl">
          Built by <span className="text-gold">nobody.</span>
          <br />
          Backed by everybody.
        </h1>
        <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-zinc-400">
          You won't find a team page. There isn't one. There is an address that
          prefers to stay silent — and a leaderboard that cannot lie.
        </p>
      </section>

      {/* Manifesto */}
      <section className="mt-16 space-y-6 rounded-2xl border border-white/10 bg-[#080808] p-8" data-testid="about-manifesto">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">The idea</div>
        <p className="text-lg leading-relaxed text-zinc-300">
          Every crypto leaderboard you've seen was paid for. Sponsored slots.
          Curated lists. Friends of friends at the top.
        </p>
        <p className="text-lg leading-relaxed text-zinc-300">
          FLIP has no slots to sell. Position is <b className="text-white">earned in public</b>:
          you back what you believe in with real money, on-chain, and the rank moves.
          That's the whole trick. There is no other trick.
        </p>
        <p className="text-lg leading-relaxed text-zinc-500">
          We don't hold your money. We don't touch your tokens. Treasuries are
          public addresses — the chain is the receipt.
        </p>
      </section>

      {/* Q&A — corto e criptico */}
      <section className="mt-10 grid gap-4 sm:grid-cols-2">
        <Card q="Who is behind FLIP?" a="An unknown address. It signed the first block and went quiet." />
        <Card q="How does FLIP make money?" a={<span className="text-zinc-400">It doesn't. Not yet. Not obviously. <span className="text-zinc-600">Watch.</span></span>} />
        <Card q="Can the ranking be bought?" a="The ranking IS bought — openly, on-chain, by anyone. That's the point." />
        <Card q="What happens to the treasuries?" a="That's between the token and its believers. We keep the receipts." />
      </section>

      {/* The oracle */}
      <section className="mt-10 rounded-2xl border border-gold/20 bg-gold/[0.04] p-8 text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] text-gold/70">The oracle</div>
        <p className="mt-3 text-sm text-zinc-500">
          It knows something about FLIP. It speaks once per question.
        </p>
        <button
          onClick={askOracle} data-testid="oracle-button"
          className="mt-5 inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-gold transition-colors hover:bg-gold/10"
        >
          <Eye className="h-4 w-4" /> Ask the oracle
        </button>
        <div className="mt-5 min-h-[28px] font-mono text-sm text-gold/90" data-testid="oracle-answer">
          {whisper && `"${whisper}"`}
        </div>
      </section>

      {/* Redacted roadmap */}
      <section className="mt-10 rounded-2xl border border-white/10 bg-[#080808] p-8">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">What's coming</div>
          <button
            onClick={() => setRevealed((r) => !r)} data-testid="reveal-button"
            className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500 transition-colors hover:text-white"
          >
            {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {revealed ? "Hide" : "Try to reveal"}
          </button>
        </div>
        <ul className="mt-4 space-y-3 font-mono text-sm">
          {REDACTED.map((block, i) => (
            <li key={i} className="flex items-center gap-3 text-zinc-500" data-testid={`redacted-${i}`}>
              <span className="text-gold/60">0{i + 1}</span>
              {revealed ? (
                <span className="text-zinc-300">
                  {["Something for the callers.", "Something for the crowns.", "Something nobody asked for."][i]}
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-600">(allegedly)</span>
                </span>
              ) : (
                <span className="select-none tracking-widest text-zinc-700">{block}</span>
              )}
            </li>
          ))}
        </ul>
        {revealed && (
          <p className="mt-4 text-[11px] text-zinc-600">
            You didn't read that here.
          </p>
        )}
      </section>

      {/* CTA */}
      <section className="mt-14 flex flex-col items-center gap-4 text-center">
        <Link to="/" data-testid="about-cta"
          className="flex items-center gap-2 rounded-full bg-white px-8 py-3.5 font-display text-sm font-black uppercase tracking-wide text-black transition-transform hover:bg-zinc-200 active:scale-95">
          Take the top <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/rules" className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white">
          <ScrollText className="h-3.5 w-3.5" /> Read the rules — the only document that tells everything
        </Link>
      </section>
    </div>
  );
}

function Card({ q, a }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#080808] p-6">
      <div className="font-display text-base font-bold text-white">{q}</div>
      <div className="mt-2 text-sm leading-relaxed">{a}</div>
    </div>
  );
}
